// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  isEquipmentRunning,
  overallStatusToTagType,
  overallStatusToText,
} from '../../../../../../plugins/crusher';

// Types
import { Theme } from '../../../../../../types/theme';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { ModuleHeader } from '../../../../../../layout/elements/ModuleHeader';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import { Speedometer } from '../../../../../../layout/elements/Speedometer';
import { Tag } from '../../../../../../layout/elements/Tag';

// View
import {
  DriveSpeedContainer,
  Motor1AmpsContainer,
  Motor2AmpsContainer,
  MotorStatusContainer,
  OverallStatusContainer,
  ShaftRpmContainer,
} from './MotorStatus.style';
import { MotorStatusProps } from './MotorStatus.types';

function MotorStatus({
  className,
  unitData,
  edgeStatus,
  onOpenDrawer,
  testId,
}: MotorStatusProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  const speedometerCount = useMemo((): number => {
    let count: number = 2;
    if (unitData?.crusherSpeedMonitorEnable) count += 1;
    if (unitData?.crusherMotorQty) count += 1;
    return count;
  }, [unitData?.crusherSpeedMonitorEnable, unitData?.crusherMotorQty]);

  const getSpeedometerColSpan = (index: number): {
    xl: number;
    md: number;
    sm: number;
    xs: number;
  } => {
    const desktopCols: number = 24 / speedometerCount;

    if (speedometerCount === 3 && index === 2) {
      return {
        xl: desktopCols, md: desktopCols, sm: 24, xs: 24,
      };
    }

    return {
      xl: desktopCols, md: desktopCols, sm: 12, xs: 12,
    };
  };

  const getSpeedometerPosition = (index: number): { isLastInRow: boolean; isLastRow: boolean } => {
    if (speedometerCount === 3 && index === 2) {
      return { isLastInRow: true, isLastRow: true };
    }

    const itemsPerRow: number = 2;
    const totalRows: number = Math.ceil(speedometerCount / itemsPerRow);
    const currentRow: number = Math.floor(index / itemsPerRow) + 1;
    const positionInRow: number = (index % itemsPerRow) + 1;

    return {
      isLastInRow: positionInRow === itemsPerRow || index === speedometerCount - 1,
      isLastRow: currentRow === totalRows,
    };
  };

  return (
    <MotorStatusContainer
      data-testid={testId}
      className={className}
    >
      <Row>
        <Col>
          <ModuleHeader
            onClick={onOpenDrawer}
            title={t('view.crusher.vsi.motorStatus.title')}
            testId={`${testId}-module-header`}
          />
        </Col>
      </Row>

      {/* Overall Status */}
      <Row>
        <Col cols={24}>
          <OverallStatusContainer>
            <Paragraph
              size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.statusTitleColor}
            >
              {t('view.crusher.vsi.motorStatus.overallStatus')}
            </Paragraph>
            <Tag
              text={overallStatusToText(unitData?.crusherMotorOverallStatus)}
              type={overallStatusToTagType(
                unitData?.crusherMotorOverallStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
              testId="crusher-motor-overall-status-tag"
            />
          </OverallStatusContainer>
        </Col>
      </Row>

      {/* Speedometers */}
      <Row style={{ flex: 1 }}>
        {unitData?.crusherSpeedMonitorEnable && (
          <Col
            xl={getSpeedometerColSpan(0).xl}
            md={getSpeedometerColSpan(0).md}
            sm={getSpeedometerColSpan(0).sm}
            xs={getSpeedometerColSpan(0).xs}
          >
            <ShaftRpmContainer
              isLastInRow={getSpeedometerPosition(0).isLastInRow}
              isLastRow={getSpeedometerPosition(0).isLastRow}
            >
              <Speedometer
                title={`${t('view.crusher.vsi.motorStatus.shaftRpm')}`}
                value={unitData?.crusherMotorRPM}
                unit="rpm"
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                minValue={0}
                maxValue={3000}
                thresholds={{
                  lowWarning: unitData?.spBeltSlipAlarmRpm,
                }}
                testId="crusher-motor-rpm-speedometer"
              />
            </ShaftRpmContainer>
          </Col>
        )}

        <Col
          xl={getSpeedometerColSpan(unitData?.crusherSpeedMonitorEnable ? 1 : 0).xl}
          md={getSpeedometerColSpan(unitData?.crusherSpeedMonitorEnable ? 1 : 0).md}
          sm={getSpeedometerColSpan(unitData?.crusherSpeedMonitorEnable ? 1 : 0).sm}
          xs={getSpeedometerColSpan(unitData?.crusherSpeedMonitorEnable ? 1 : 0).xs}
        >
          <Motor1AmpsContainer
            isLastInRow={
              getSpeedometerPosition(unitData?.crusherSpeedMonitorEnable ? 1 : 0).isLastInRow
            }
            isLastRow={
              getSpeedometerPosition(unitData?.crusherSpeedMonitorEnable ? 1 : 0).isLastRow
            }
          >
            <Speedometer
              title={`${t('view.crusher.vsi.motorStatus.motor1Amps')}`}
              value={unitData?.crusherMotor1Amps}
              unit="A"
              minValue={0}
              maxValue={750}
              thresholds={{
                highWarning: unitData?.spCrusherMotorFullLoadAmps,
              }}
              running={
                isEquipmentRunning(
                  edgeStatus,
                  unitData?.timestamp,
                  DEVICE_COMMUNICATION_THRESHOLD,
                )
              }
              testId="crusher-motor-1-amps-speedometer"
            />
          </Motor1AmpsContainer>
        </Col>

        {unitData?.crusherMotorQty && (
          <Col
            xl={getSpeedometerColSpan(unitData?.crusherSpeedMonitorEnable ? 2 : 1).xl}
            md={getSpeedometerColSpan(unitData?.crusherSpeedMonitorEnable ? 2 : 1).md}
            sm={getSpeedometerColSpan(unitData?.crusherSpeedMonitorEnable ? 2 : 1).sm}
            xs={getSpeedometerColSpan(unitData?.crusherSpeedMonitorEnable ? 2 : 1).xs}
          >
            <Motor2AmpsContainer
              isLastInRow={
                getSpeedometerPosition(
                  unitData?.crusherSpeedMonitorEnable ? 2 : 1,
                ).isLastInRow
              }
              isLastRow={
                getSpeedometerPosition(
                  unitData?.crusherSpeedMonitorEnable ? 2 : 1,
                ).isLastRow
              }
            >
              <Speedometer
                title={`${t('view.crusher.vsi.motorStatus.motor2Amps')}`}
                value={unitData?.crusherMotor2Amps}
                unit="A"
                minValue={0}
                maxValue={750}
                thresholds={{
                  highWarning: unitData?.spCrusherMotorFullLoadAmps,
                }}
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                testId="crusher-motor-2-amps-speedometer"
              />
            </Motor2AmpsContainer>
          </Col>
        )}

        <Col
          xl={getSpeedometerColSpan(speedometerCount - 1).xl}
          md={getSpeedometerColSpan(speedometerCount - 1).md}
          sm={getSpeedometerColSpan(speedometerCount - 1).sm}
          xs={getSpeedometerColSpan(speedometerCount - 1).xs}
        >
          <DriveSpeedContainer
            isLastInRow={getSpeedometerPosition(speedometerCount - 1).isLastInRow}
            isLastRow={getSpeedometerPosition(speedometerCount - 1).isLastRow}
          >
            <Speedometer
              title={`${t('view.crusher.vsi.motorStatus.driveSpeed')}`}
              minValue={0}
              maxValue={10}
              value={unitData?.driveSpeedHz}
              unit="Hz"
              running={
                isEquipmentRunning(
                  edgeStatus,
                  unitData?.timestamp,
                  DEVICE_COMMUNICATION_THRESHOLD,
                )
              }
              thresholds={{}}
              testId="drive-speed-speedometer"
            />
          </DriveSpeedContainer>
        </Col>

      </Row>
    </MotorStatusContainer>
  );
}

export { MotorStatus };
